import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';
import { validatePassword } from '../../../utils/passwordValidation';
import './LoginPage.css';
import AppLogo from '../../../components/common/AppLogo';

/** Query param name in reset email links: /reset-password?forgotpassword-token=... */
export const FORGOT_PASSWORD_TOKEN_PARAM = 'forgotpassword-token';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token =
    searchParams.get(FORGOT_PASSWORD_TOKEN_PARAM) ||
    searchParams.get('token') ||
    '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      return setError('Invalid or missing link. Please use the link from your reset email.');
    }

    const pwdError = validatePassword(password);
    if (pwdError) return setError(pwdError);

    if (password !== confirmPassword) {
      return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      await authService.resetForgotPassword(token, password);
      setSuccess('Password reset successfully. Redirecting to sign in…');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Could not reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div
        className="login-left"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/loginimage.jpeg)` }}
      >
        <div className="login-overlay" />
        <div className="login-logo">
          <AppLogo variant="default" className="login-logo-img" />
          <div className="login-logo-name">
            Indusinformatics Structural Health Monitoring©
          </div>
        </div>
        <div className="login-features">
          <div className="login-feature">
            <div className="login-feature-icon">
              <i className="ti ti-lock" />
            </div>
            <div>
              <h4>Create a new password</h4>
              <p>Choose a strong password to regain access to your Indus SHM account.</p>
            </div>
          </div>
          <div className="login-left-footer">
            <p className="login-left-security">
              <i className="ti ti-shield-check" />
              Your password is encrypted and stored securely
            </p>
            <p className="login-left-copyright">
              &copy; 2026 Indus Informatics. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-card">
          <div className="login-icon-wrap">
            <i className="ti ti-key" style={{ fontSize: 28, color: '#1a4fa8' }} />
          </div>

          <h2>Reset Password</h2>
          <p className="login-subtitle">Enter and confirm your new password</p>

          {error && (
            <div className="login-error">
              <i className="ti ti-alert-circle" /> {error}
            </div>
          )}
          {success && (
            <div className="login-success">
              <i className="ti ti-circle-check" /> {success}
            </div>
          )}

          {!token && !success && (
            <div className="login-error">
              <i className="ti ti-alert-circle" /> Missing reset token. Open the link from your email.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label>New Password</label>
              <div className="login-input-wrap">
                <i className="ti ti-lock" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={!token || !!success}
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowPwd((p) => !p)}
                  tabIndex={-1}
                >
                  <i className={`ti ${showPwd ? 'ti-eye-off' : 'ti-eye'}`} />
                </button>
              </div>
            </div>

            <div className="login-field">
              <label>Confirm New Password</label>
              <div className="login-input-wrap">
                <i className="ti ti-lock" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={!token || !!success}
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowConfirm((p) => !p)}
                  tabIndex={-1}
                >
                  <i className={`ti ${showConfirm ? 'ti-eye-off' : 'ti-eye'}`} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !token || !!success}
            >
              {loading ? (
                <>
                  <i className="ti ti-loader" /> Saving…
                </>
              ) : (
                <>
                  <i className="ti ti-check" /> Reset Password
                </>
              )}
            </button>
          </form>

          <p className="set-password-back">
            <Link to="/login">Back to Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
